'use client';

import { useEffect, useState } from 'react';

export function usePageLoader() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Hard maximum timeout - loader will ALWAYS dismiss after this
    const hardTimeout = setTimeout(() => {
      if (isMounted) setIsLoading(false);
    }, 3000);

    const checkAllImagesLoaded = async () => {
      // Wait for document to be ready - handle race condition where load already fired
      if (document.readyState !== 'complete') {
        await new Promise<void>((resolve) => {
          const onLoad = () => resolve();
          window.addEventListener('load', onLoad, { once: true });
          // Safety: if load fires before we attach, readyState will be complete
          if (document.readyState === 'complete') {
            window.removeEventListener('load', onLoad);
            resolve();
          }
        });
      }

      // Small delay to ensure React hydration is complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Only wait for images that are already in the DOM and NOT lazy-loaded
      const waitForImages = async () => {
        const allImages = Array.from(document.querySelectorAll('img'));
        
        // Filter to only eagerly-loaded images (skip lazy ones not yet visible)
        const eagerImages = allImages.filter((img) => {
          return img.loading !== 'lazy' || img.complete;
        });

        if (eagerImages.length === 0) {
          return true;
        }

        const imagePromises = eagerImages.map((img) => {
          if (img.complete && img.naturalHeight !== 0) {
            return Promise.resolve();
          }

          return new Promise<void>((resolve) => {
            // Per-image timeout of 2s so a single slow image can't block forever
            const imgTimeout = setTimeout(resolve, 2000);

            const cleanup = () => {
              clearTimeout(imgTimeout);
              img.removeEventListener('load', onLoad);
              img.removeEventListener('error', onError);
            };

            const onLoad = () => { cleanup(); resolve(); };
            const onError = () => { cleanup(); resolve(); };

            img.addEventListener('load', onLoad);
            img.addEventListener('error', onError);
          });
        });

        await Promise.all(imagePromises);
        return true;
      };

      await waitForImages();

      // Small delay for smooth transition
      if (isMounted) {
        clearTimeout(hardTimeout);
        setTimeout(() => {
          if (isMounted) setIsLoading(false);
        }, 200);
      }
    };

    checkAllImagesLoaded();

    return () => {
      isMounted = false;
      clearTimeout(hardTimeout);
    };
  }, []);

  return isLoading;
}